/* ───────────────────────────────────────────────────────────────────────────
   O16 Pre-order / Back-order — live cart + variant sync (FEATURE 1,
   SEPARATE module from o16-pdp-reassurance.js's free-shipping sync — same
   mechanism, own file so the two features never get tangled).

   PROBLEM 1 (cart-driven): snippets/o16-cart-reassurance.liquid resolves
   THREE states (A: this product IS pre/back-order · B: cart holds a
   pre/back-order item · C: neither) and renders the copy for line 1 + line 2
   + the explanatory note. The cart drawer's copy updates live after every
   AJAX cart mutation because cartDrawer.renderContents() fully re-renders
   the cart-drawer section server-side. The PDP copy (inside
   [data-o16-pdp-reassurance], rendered from
   snippets/main-product-section.liquid) has no such re-render path — it is
   server-rendered once at page load and would go stale (e.g. still show
   State C after a pre-order item is added to the cart from elsewhere)
   until a full reload.

   FIX 1: subscribe to PUB_SUB_EVENTS.cartUpdate (same event product-form.js /
   assets/cart.js / o16-cross-sell.js's addToCart all publish after every
   cart add/remove/quantity change) and recompute the state client-side:
     - "self" half of the state (is THIS page's product/variant itself
       pre/back-order?) is baked into data-pb-self/-type/-date on the PDP's
       own <o16-dispatch-countdown>. type/date are PRODUCT-level per slot
       (constant across variants), so only data-pb-self can go stale — and
       only from a variant switch (see PROBLEM 2 below), never from a cart
       mutation alone.
     - "cart" half of the state is read from the CART DRAWER's own
       <o16-dispatch-countdown data-pb-cart-*> — guaranteed fresh because
       PUB_SUB_EVENTS.cartUpdate fires AFTER cartDrawer.renderContents() has
       replaced #CartDrawer's contents with a fresh Section-Rendering-API
       render (same reasoning already documented in o16-pdp-reassurance.js).
       This also means bundle components are correctly accounted for without
       this script re-implementing that logic — the drawer's own SSR already
       ran snippets/o16-preback-cart-status.liquid, which is bundle-aware.
   Falls back to a direct /cart.js product-id match (documented,
   NON-bundle-aware, matching the existing free-shipping fallback's own
   documented limitation) only if no cart drawer exists on the page
   (settings.cart_type == 'page'; the store currently runs 'drawer').

   PROBLEM 2 (variant-driven, owner round-5, 2026-07-16): a slot's optional
   "Variants" field can cover only SOME of a product's variants. Selecting a
   different variant must toggle State A live — no reload — on BOTH the PDP
   gallery label (.o16-preback-label, snippets/o16-product-gallery.liquid)
   AND the reassurance block's line 1/2 (which is the same
   <o16-dispatch-countdown> this file already manages for cart sync).

   FIX 2: this theme dispatches a plain DOM CustomEvent named `variant:change`
   on `document` (assets/global.js, VariantSelects.onVariantChange) with
   `event.detail.variant` = the newly selected variant object — it does NOT
   use Dawn's PUB_SUB_EVENTS.variantChange (that pub/sub channel has no
   publisher anywhere in this theme; verified by search). On that event:
     - recompute data-pb-self on the reassurance <o16-dispatch-countdown> by
       testing the new variant's id against data-pb-self-covered-ids (baked
       server-side by snippets/o16-cart-reassurance.liquid), then call the
       SAME sync() used for cart updates so state A/B/C is recombined exactly
       once, in one place.
     - toggle + rewrite the gallery label the same way, using its own
       data-pb-covered-ids (baked by snippets/o16-product-gallery.liquid).
   Type/date were assumed constant across every covered variant here
   (true for the manual 5-slot system). PROBLEM 3 below replaces that
   assumption where it does not hold.

   PROBLEM 3 (per-variant date/type, owner escalation 2026-09-07, PADELLA
   case: Ø24 in stock, Ø28 back-order 2026-10-29, Ø32/Ø36 back-order
   2026-10-07 — three states, two dates, one product): the delivery-ladder
   data source (snippets/o16-preback-status.liquid RESOLUTION LEVEL 1) is
   genuinely per-VARIANT, not per-product — PROBLEM 2's "type/date never
   change" assumption silently showed the WRONG date (or none at all) after
   a variant switch on any product where it didn't hold, which also fed the
   add-to-cart consent gate (assets/o16-extended-delivery-consent.js) the
   wrong signal.

   FIX 3: snippets/main-product-section.liquid now walks EVERY variant
   through o16-preback-status ONCE per render (see
   snippets/o16-extended-delivery-map.liquid) and bakes the whole table as
   a delimited string (parseVariantMap() below) onto data-pb-self-variant-map
   (reassurance) and data-pb-variant-map (gallery label). Both are consulted
   FIRST on `variant:change`, falling back to the PROBLEM-2 covered-ids-only
   toggle only when no map was rendered — which still correctly describes
   the manual 5-slot system, where the assumption above is true.

   Runs on every product page (loaded from sections/main-product.liquid).
─────────────────────────────────────────────────────────────────────────── */

(function () {
  function getPdpWrappers() {
    return document.querySelectorAll('[data-o16-pdp-reassurance]');
  }

  /* Membership test against a comma-joined id list (data-pb-*-covered-ids /
     data-pb-covered-ids) — padded-comma contains, mirroring the same
     padding trick used server-side in snippets/o16-preback-status.liquid to
     avoid a "128" vs "1281" partial-string false positive. */
  function isCovered(idsAttr, id) {
    if (!idsAttr || id == null) return false;
    var padded = ',' + idsAttr + ',';
    return padded.indexOf(',' + id + ',') !== -1;
  }

  /* PER-VARIANT MAP (owner escalation, 2026-09-07, PADELLA case: Ø24 in
     stock, Ø28 back-order 29 Oct, Ø32/Ø36 back-order 07 Oct — three states,
     two dates, one product). isCovered() above only ever answers "is this
     variant ONE OF the ids a slot covers" — it says nothing about WHICH
     date/type apply to that specific variant, and the manual 5-slot
     system's assumption that type/date are constant across every covered
     variant (still true there) does NOT hold for the delivery-ladder
     system, where each variant carries its own. data-pb-self-variant-map /
     data-pb-variant-map (snippets/o16-cart-reassurance.liquid,
     snippets/o16-product-gallery.liquid, both fed by
     snippets/o16-extended-delivery-map.liquid) carry the FULL per-variant
     table so callers can look up the newly selected variant's own
     type/date directly instead of reusing whatever the page-load variant
     resolved to. Same delimited-string idea as lookupRestockDate() below
     (semicolon-separated entries, colon-separated fields), one extra field
     (type as well as date). Kept format-compatible rather than switching to
     JSON so every Feature-1/2 variant map in this file reads the same way. */
  function parseVariantMap(mapAttr) {
    var map = {};
    if (!mapAttr) return map;
    mapAttr.split(';').forEach(function (entry) {
      if (!entry) return;
      var sep1 = entry.indexOf(':');
      if (sep1 === -1) return;
      var id = entry.slice(0, sep1);
      var rest = entry.slice(sep1 + 1);
      var sep2 = rest.indexOf(':');
      if (sep2 === -1) return;
      var type = rest.slice(0, sep2);
      var date = rest.slice(sep2 + 1);
      if (id && type && date) map[id] = { type: type, date: date };
    });
    return map;
  }

  /* TEN-DAY CONSENT THRESHOLD (owner, 2026-09-08) — same "delegate to the
     one shared implementation" pattern as weeksUntilLabel above.
     isUnderGateThreshold/formatExtendedDate are owned by
     assets/o16-extended-delivery-consent.js (O16_EXT_GATE_THRESHOLD_DAYS +
     snippets/o16-extended-delivery-gate.liquid are the two things that
     must never drift) — never re-implemented a second time here. Fails
     toward "over the threshold" (gate required / weeks phrase) when the
     global is somehow missing, same fail-safe direction as the Liquid
     snippet's own blank-date default. */
  function isUnderGateThreshold(isoDateStr) {
    if (window.O16ExtendedDelivery && typeof window.O16ExtendedDelivery.isUnderGateThreshold === 'function') {
      return window.O16ExtendedDelivery.isUnderGateThreshold(isoDateStr);
    }
    // eslint-disable-next-line no-console
    console.error('O16 preback-sync: isUnderGateThreshold unavailable (o16-extended-delivery-consent.js not loaded).');
    return false;
  }

  function formatExtendedDate(isoDateStr) {
    if (window.O16ExtendedDelivery && typeof window.O16ExtendedDelivery.formatExtendedDate === 'function') {
      return window.O16ExtendedDelivery.formatExtendedDate(isoDateStr);
    }
    // eslint-disable-next-line no-console
    console.error('O16 preback-sync: formatExtendedDate unavailable (o16-extended-delivery-consent.js not loaded).');
    return '';
  }

  /* Preferred path — read the already-fresh, bundle-aware cart aggregate the
     cart drawer just re-rendered server-side (o16-preback-cart-status.liquid).
     Returns {inCart, type, date} or null if no cart drawer is on this page. */
  function readCartPbFromDrawer() {
    var el = document.querySelector('cart-drawer [data-pb-cart-in-cart]');
    if (!el) return null;
    return {
      inCart: el.getAttribute('data-pb-cart-in-cart') === 'true',
      type: el.getAttribute('data-pb-cart-type') || '',
      date: el.getAttribute('data-pb-cart-date') || '',
      // TEN-DAY THRESHOLD (owner, 2026-09-08) — the CART AGGREGATE's own
      // gate (snippets/o16-preback-cart-status.liquid, computed once on
      // the "latest date wins" winner, never per line). Defaults to true
      // (weeks phrase, "in " connector) if the attribute is somehow
      // missing — same fail-safe direction as the Liquid source of truth.
      gate: el.getAttribute('data-pb-cart-gate') !== 'false'
    };
  }

  /* Resolve state A/B/C + the copy to show, combining the wrapper's STATIC
     self-* attributes with the (possibly fresh) cartPb reading. Mirrors the
     server-side resolution in snippets/o16-cart-reassurance.liquid exactly,
     INCLUDING the ten-day-threshold gate (owner, 2026-09-08) that decides
     whether line2Date is a week count ("in 5 weeks") or an exact date
     ("Wed 17 Sept", no "in") — see snippets/o16-extended-delivery-
     gate.liquid. */
  function resolveState(countdown, cartPb) {
    var selfMatched = countdown.getAttribute('data-pb-self') === 'true';
    var selfType = countdown.getAttribute('data-pb-self-type') || '';
    var selfDate = countdown.getAttribute('data-pb-self-date') || '';
    var selfGate = countdown.getAttribute('data-pb-self-gate') !== 'false';

    if (selfMatched) {
      var line2Date = selfDate;
      var line2Gate = selfGate;
      // Latest date wins — once anything is genuinely in the cart, its date
      // AND ITS OWN GATE (a mixed cart follows the slowest item — see
      // snippets/o16-preback-cart-status.liquid's own PASS 1/2 header)
      // supersede this product's own pre-add values.
      if (cartPb && cartPb.inCart && cartPb.date) {
        line2Date = cartPb.date;
        line2Gate = cartPb.gate;
      }
      return { state: 'A', line1Type: selfType, line2Date: line2Date, line2Gate: line2Gate };
    }
    if (cartPb && cartPb.inCart) {
      return { state: 'B', line1Type: cartPb.type, line2Date: cartPb.date, line2Gate: cartPb.gate };
    }
    return { state: 'C', line1Type: '', line2Date: '', line2Gate: true };
  }

  function applyState(pdpWrapper, countdown, cartPb) {
    var resolved = resolveState(countdown, cartPb);
    var state = resolved.state;
    var line1Type = resolved.line1Type;
    var line2Date = resolved.line2Date;
    var line2Gate = resolved.line2Gate;

    var block = countdown.querySelector('[data-o16-dispatch-block]');
    if (block) block.setAttribute('data-pb-state', state);

    var line1Normal = countdown.querySelector('[data-o16-line1-normal]');
    var line1Pb = countdown.querySelector('[data-o16-line1-pb]');
    // FEATURE 1 — owner round-3: line 2's "normal" half (label + JS-filled
    // date) is now grouped in its own wrapper (mirrors line 1's
    // [data-o16-line1-normal]) so it toggles as one unit — see
    // snippets/o16-cart-reassurance.liquid for the full rationale.
    var line2Normal = countdown.querySelector('[data-o16-line2-normal]');
    var line2Pb = countdown.querySelector('[data-o16-line2-pb]');
    // FEATURE 1 — owner round-3: the explanatory note was DELETED from the
    // cart drawer entirely (PDP only). `note` is simply absent from the DOM
    // there, so this lookup naturally no-ops outside the PDP — nothing else
    // needs to change: getPdpWrappers() only ever queries
    // [data-o16-pdp-reassurance], which only exists on the PDP anyway.
    var note = pdpWrapper.querySelector('[data-o16-preback-note]');

    var isNormal = state === 'C';
    if (line1Normal) line1Normal.hidden = !isNormal;
    if (line2Normal) line2Normal.hidden = !isNormal;
    if (note) note.hidden = isNormal;

    if (line1Pb) {
      line1Pb.hidden = isNormal;
      if (state === 'A') {
        line1Pb.textContent = 'THIS IS A ' + line1Type;
      } else if (state === 'B') {
        line1Pb.textContent = 'YOU HAVE ADDED A ' + line1Type + ' PRODUCT IN YOUR CART';
      } else {
        line1Pb.textContent = '';
      }
    }

    if (line2Pb) {
      line2Pb.hidden = isNormal;
      // FEATURE 1 — owner round-3: line 2 must read as ONE continuous
      // sentence — a single text node in one element, matching the SSR
      // markup exactly, so it wraps as prose rather than as two
      // disconnected flex-wrapped blocks.
      // owner round-4: State B's "(due to ... purchase)" parenthetical was
      // pushing this line to 3 lines in the cart drawer — DELETED (line 1
      // already explains why the date is later). States A and B now render
      // the exact same short shape: "Estimated delivery <date>" — CSS
      // (o16-cart-drawer.css, [data-o16-line2-pb]) forces it onto one line
      // via white-space:nowrap now that it's short enough to fit at 375px.
      // DURATION OR DATE (owner, 2026-09-07; ten-day threshold + "approx."
      // removal, 2026-09-08): line2Date is already the FINAL display value
      // by the time it reaches here (SSR bakes it that way; onVariantChange's
      // per-variant-map branch below computes it the same way on a live
      // variant switch) — either a weeks phrase (line2Gate true) or an
      // exact calendar date (line2Gate false), mirroring the SSR sentence
      // exactly (snippets/o16-cart-reassurance.liquid, data-o16-line2-pb).
      // "in " only reads naturally before a week count, never before a
      // date ("Estimated delivery Wed 17 Sept", not "...in Wed 17 Sept").
      if (state === 'A' || state === 'B') {
        line2Pb.textContent = 'Estimated shipping ' + line2Date;
      } else {
        line2Pb.textContent = '';
      }
    }
  }

  /* Fallback path — direct /cart.js product-id match against the ≤5 slot ids
     baked onto the countdown element (data-pb-slot-ids/-types/-dates, comma
     joined, same index across all slots). NOT bundle-aware (/cart.js never
     exposes item_components) — only used when no cart drawer exists on the
     page to read a bundle-aware aggregate from. */
  function syncFromCartJs(pdpWrapper, countdown) {
    var slotIds = (countdown.getAttribute('data-pb-slot-ids') || '').split(',');
    var slotTypes = (countdown.getAttribute('data-pb-slot-types') || '').split(',');
    var slotDates = (countdown.getAttribute('data-pb-slot-dates') || '').split(',');
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var items = cart.items || [];
        // TEN-DAY THRESHOLD (owner, 2026-09-08): gate defaults to true
        // (weeks phrase, consent required) in this fallback — no raw date/
        // epoch is available client-side here (see the "No epoch available"
        // comment below, a pre-existing documented limitation of this
        // /cart.js-only path), so there is nothing to compare against the
        // threshold with. Same fail-safe direction as every other blank/
        // unknown-date default in this feature. This path is only reached
        // when no cart drawer exists on the page at all (settings.cart_type
        // == 'page'); the store currently runs 'drawer', so it is inert in
        // production today.
        var cartPb = { inCart: false, type: '', date: '', gate: true };
        slotIds.forEach(function (id, i) {
          if (!id || !slotTypes[i]) return;
          var found = items.some(function (item) {
            return String(item.product_id) === String(id);
          });
          if (found) {
            cartPb.inCart = true;
            // No epoch available client-side in this fallback path — last
            // matching slot wins (documented simplification; the primary
            // drawer-read path above already does a true "latest date"
            // comparison server-side and is used whenever a drawer exists).
            cartPb.type = slotTypes[i];
            cartPb.date = slotDates[i] || '';
          }
        });
        applyState(pdpWrapper, countdown, cartPb);
      })
      .catch(function () {
        /* Network hiccup — leave the PDP copy as last rendered rather than guess. */
      });
  }

  function sync() {
    var pdpWrappers = getPdpWrappers();
    if (!pdpWrappers.length) return;

    var cartPb = readCartPbFromDrawer();

    pdpWrappers.forEach(function (pdpWrapper) {
      var countdown = pdpWrapper.querySelector('o16-dispatch-countdown');
      if (!countdown) return;
      if (cartPb !== null) {
        applyState(pdpWrapper, countdown, cartPb);
      } else {
        syncFromCartJs(pdpWrapper, countdown);
      }
    });
  }

  /* ── PROBLEM 2 — variant-change reactivity (owner round-5) ──────────────── */

  /* Toggle + rewrite the PDP gallery label (.o16-preback-label,
     snippets/o16-product-gallery.liquid) for the newly selected variant.
     Type/date are product-level per slot and never change here — only
     matched/hidden does. Element is ALWAYS in the DOM (rendered hidden when
     not matched), so this only ever needs to flip visibility + text, never
     construct new markup.

     BUG FIX (owner round-9, 2026-07-17): the theme renders the ENTIRE
     product section TWICE — .main-product-desktop-section-wrapper AND
     .main-product-mobile-section-wrapper (one is display:none per
     breakpoint) — so the gallery, and this label with it, exists TWICE in
     the DOM. A plain querySelector only ever found/updated the FIRST
     (desktop) copy; on mobile that one is the hidden wrapper, so the
     shopper never saw the visible (mobile) label update. querySelectorAll +
     forEach updates every copy identically, matching the pattern
     getPdpWrappers() already used for the reassurance block. */
  function applyGalleryLabel(variantId) {
    var labels = document.querySelectorAll('[data-o16-preback-label]');
    labels.forEach(function (label) {
      // PER-VARIANT MAP (owner escalation, 2026-09-07, PADELLA case) — the
      // map is authoritative when present (it carries every variant's OWN
      // type/date, not just whichever one covered_ids happened to list at
      // page load); isCovered()/data-pb-covered-ids stays as a fallback for
      // any render path that hasn't been given a map (there is currently
      // none, but this fails toward "keep the old, still-correct-for-the-
      // manual-system behaviour" rather than toward silence).
      var map = parseVariantMap(label.getAttribute('data-pb-variant-map'));
      var entry = variantId != null ? map[String(variantId)] : null;
      var matched;
      var type;
      var date;
      if (Object.keys(map).length) {
        // entry.date is the map's RAW ISO date (see the file header) — the
        // no-map fallback below reads data-pb-date, which SSR already
        // bakes as the correctly-gated display value (weeks phrase OR
        // calendar date — snippets/o16-product-gallery.liquid), so both
        // branches must end up with the SAME kind of value here.
        //
        // TEN-DAY THRESHOLD (owner, 2026-09-08): recomputed here, per
        // variant, exactly like the PDP add-to-cart gate
        // (assets/o16-extended-delivery-consent.js's syncExtendedFlagForVariant)
        // — a variant switch has no server round trip, so the NEWLY
        // selected variant's own date must be re-checked against the same
        // shared threshold rather than reusing whatever the page-load
        // variant resolved to. See snippets/o16-extended-delivery-gate.liquid.
        matched = !!entry;
        type = entry ? entry.type : '';
        if (entry) {
          date = formatExtendedDate(entry.date);
        } else {
          date = '';
        }
      } else {
        var coveredIds = label.getAttribute('data-pb-covered-ids');
        matched = isCovered(coveredIds, variantId);
        type = label.getAttribute('data-pb-type') || '';
        date = label.getAttribute('data-pb-date') || '';
      }
      label.hidden = !matched;
      if (matched) {
        // ONE LINE AT 375px (owner, 2026-09-08) — "TYPE: EXPECTED IN
        // APPROX. N WEEKS" wrapped to 2 lines on real devices; shortened to
        // "TYPE: DELIVERY <value>" (owner, 2026-09-08: "APPROX." dropped
        // theme-wide; <value> is a week count OR a calendar date depending
        // on the 10-day threshold — see snippets/o16-extended-delivery-
        // gate.liquid), mirrors the SSR label text exactly
        // (snippets/o16-product-gallery.liquid, .o16-preback-label).
        // Verified at both digit widths (5 / 13 weeks), a real date, and
        // both types.
        // TYPE ONLY (owner, 2026-09-16) — mirrors the SSR pill in
        // snippets/o16-product-gallery.liquid; the date lives in the buy zone.
        label.textContent = type;
        label.setAttribute('aria-label', type);
      }
    });
  }

  /* FEATURE 2 (Expected re-stock) — owner round-6, 2026-07-17. DISPLAY ONLY,
     SEPARATE from Feature 1 — this is the ONLY JS this feature has; there is
     no reassurance/cart-state coupling at all, just the same
     toggle-a-label-on-variant-change mechanism as applyGalleryLabel above,
     against the parallel .o16-restock-label element
     (snippets/o16-product-gallery.liquid) and its own
     data-rs-covered-ids/-date/-variant-dates/-label attributes.

     BUG FIX (owner round-9, 2026-07-17): same desktop/mobile duplicate-DOM
     bug as applyGalleryLabel above (the product section renders twice —
     .main-product-desktop-section-wrapper +
     .main-product-mobile-section-wrapper) — querySelectorAll + forEach
     updates every copy of this label, not just the first (desktop) one. */
  function applyRestockLabel(variantId) {
    var labels = document.querySelectorAll('[data-o16-restock-label]');
    labels.forEach(function (label) {
      var coveredIds = label.getAttribute('data-rs-covered-ids');
      var matched = isCovered(coveredIds, variantId);
      label.hidden = !matched;
      if (matched) {
        var rsLabel = label.getAttribute('data-rs-label') || 'EXPECTED RE-STOCK';
        var date = lookupRestockDate(label, variantId);
        label.textContent = rsLabel.toUpperCase() + ': ' + date.toUpperCase();
        label.setAttribute('aria-label', rsLabel + ': ' + date);
      }
    });
  }

  /* Round-19 (2026-07-22): the SAME product can occupy MULTIPLE re-stock
     slots with a DIFFERENT date per variant (e.g. Padella Ø28 = "Medio
     October" in slot 1, Ø32 = "Medio September" in slot 2 —
     config/settings_schema.json "O16 — Expected re-stock"). Looks up the
     NEWLY selected variant's own date from data-rs-variant-dates
     ("id:date;id:date", built server-side by
     snippets/o16-restock-status.liquid — each entry split on the FIRST ":"
     only, since a free-text date like "Medio October" never contains one
     but must never be mistaken for a delimiter if it somehow did). Falls
     back to the single data-rs-date (the page-load variant's own date) if
     the map is absent/empty or simply doesn't list this variant —
     defensive, keeps the label showing something sensible rather than
     going blank. */
  function lookupRestockDate(label, variantId) {
    var map = label.getAttribute('data-rs-variant-dates');
    var fallback = label.getAttribute('data-rs-date') || '';
    if (!map) return fallback;
    var entries = map.split(';');
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      var sepIndex = entry.indexOf(':');
      if (sepIndex === -1) continue;
      var id = entry.slice(0, sepIndex);
      var date = entry.slice(sepIndex + 1);
      if (String(variantId) === id) return date;
    }
    return fallback;
  }

  /* FEATURE 2 companion — "Expected re-stock: <date>" note paired with the
     o16_provenance tagline (snippets/main-product-section.liquid, 2026-08
     fix). SAME mechanism as applyRestockLabel above (isCovered +
     lookupRestockDate against data-rs-covered-ids/-date/-variant-dates —
     nothing new invented), just a second consumer of it with its own
     sentence-case text template (no uppercase transform) and its own
     pre-capitalized label baked server-side into data-rs-note-label
     (snippets/main-product-section.liquid already runs it through Liquid's
     `capitalize` filter, so this never re-implements string casing in JS).
     Same desktop/mobile duplicate-DOM handling (querySelectorAll+forEach). */
  function applyRestockNote(variantId) {
    var notes = document.querySelectorAll('[data-o16-restock-note]');
    notes.forEach(function (note) {
      var coveredIds = note.getAttribute('data-rs-covered-ids');
      var matched = isCovered(coveredIds, variantId);
      note.hidden = !matched;
      if (matched) {
        var label = note.getAttribute('data-rs-note-label') || 'Expected re-stock';
        var date = lookupRestockDate(note, variantId);
        note.textContent = label + ': ' + date;
      }
    });
  }

  function onVariantChange(event) {
    var variant = event && event.detail && event.detail.variant;
    var variantId = variant ? variant.id : null;

    // Reassurance block: recompute State A eligibility for the NEW variant,
    // then let the normal sync() combine it with whatever cart info is
    // currently known — single source of truth for state resolution,
    // whether triggered by a cart mutation or a variant switch.
    //
    // PER-VARIANT MAP (owner escalation, 2026-09-07, PADELLA case) — when
    // data-pb-self-variant-map is present, it is authoritative for BOTH
    // whether the new variant matches AND its own type/date (a
    // delivery-ladder variant's date is its own, not shared with its
    // siblings — the old covered-ids-only toggle left type/date frozen at
    // whatever the page-load variant resolved to, which is wrong here).
    // Falls back to the covered-ids-only toggle (type/date left as-is) when
    // no map was rendered, matching the old behaviour exactly for the
    // manual 5-slot system, where type/date genuinely are constant across
    // every covered variant.
    getPdpWrappers().forEach(function (pdpWrapper) {
      var countdown = pdpWrapper.querySelector('o16-dispatch-countdown');
      if (!countdown) return;
      var map = parseVariantMap(countdown.getAttribute('data-pb-self-variant-map'));
      if (Object.keys(map).length) {
        var entry = variantId != null ? map[String(variantId)] : null;
        countdown.setAttribute('data-pb-self', entry ? 'true' : 'false');
        if (entry) {
          countdown.setAttribute('data-pb-self-type', entry.type);
          // DURATION OR DATE (owner, 2026-09-07; ten-day threshold added
          // 2026-09-08): entry.date is the map's RAW ISO date;
          // data-pb-self-date is what resolveState()/applyState() read as
          // line2Date and print verbatim ("Estimated delivery [in] <line2Date>"),
          // so it must already be the FINAL display value here — a week
          // count when the newly selected variant's own date is over the
          // 10-day threshold, or the exact calendar date when within it —
          // exactly what the SSR value it replaces already was at page
          // load (snippets/o16-cart-reassurance.liquid's o16_pb_self_display).
          // data-pb-self-gate records WHICH of those two this is, so
          // applyState() knows whether to prefix "in ".
          var selfUnderThreshold = isUnderGateThreshold(entry.date);
          countdown.setAttribute(
            'data-pb-self-date',
            formatExtendedDate(entry.date)
          );
          countdown.setAttribute('data-pb-self-gate', selfUnderThreshold ? 'false' : 'true');
        }
      } else {
        var coveredIds = countdown.getAttribute('data-pb-self-covered-ids');
        var matched = isCovered(coveredIds, variantId);
        countdown.setAttribute('data-pb-self', matched ? 'true' : 'false');
      }
    });
    sync();

    // FEATURE 2 (Expected re-stock) — independent region of the page,
    // updated directly. No cart/reassurance involvement whatsoever.
    applyRestockLabel(variantId);
    applyRestockNote(variantId);

    // PDP gallery label — independent region of the page, updated directly.
    applyGalleryLabel(variantId);
  }

  function init() {
    // Registered unconditionally (doesn't depend on subscribe/PUB_SUB_EVENTS
    // — this theme dispatches `variant:change` as a plain DOM CustomEvent,
    // see the file header). Safe to call twice if init() retries below:
    // addEventListener with the same function reference is a no-op the
    // second time.
    document.addEventListener('variant:change', onVariantChange);

    if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') {
      document.addEventListener('DOMContentLoaded', init, { once: true });
      return;
    }
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      // Small delay to let the DOM settle after renderContents — same
      // defensive pattern as o16-pdp-reassurance.js / o16FillDeliveryDate.
      setTimeout(sync, 100);
    });
  }

  init();
})();
